// 260923 E1-P1 — event dạng CHUỖI NHỊP ở FE: dựng chuỗi nhịp xen thoại cho EventCard + bản FE của luật gộp/hook
// (CÙNG luật, cùng bộ case test với BE `admin-service/src/utils/filmBeatPacking.ts`).
//
// Tách khỏi file import antd — antd v6 + react-router v7 không load được dưới jest của react-scripts (CLAUDE.md).
// Pure: không gọi API, không đụng state React.
import type { FilmActionBeat, FilmDialogueLine, FilmEventBeatMeta } from '../../types/film';

// Lý do từ chối — CHỮ Y HỆT BE (ConflictError 409) để người dùng thấy cùng một câu dù chặn ở nháp hay ở server.
export const MERGE_REJECT_HOOK = 'Không gộp được: event cuối tập (hook) phải đứng riêng — luật event dạng chuỗi.';
export const MERGE_REJECT_MIXED = 'Không gộp được event dạng chuỗi (v2) với event dạng cũ (v1).';
/** E1-P2 (R8): event v2 tách theo shot ⇒ từ chối kèm hướng dẫn. CHỮ Y HỆT BE `filmBeatPacking.SPLIT_REJECT_V2`. */
export const SPLIT_REJECT_V2 = 'Event dạng chuỗi tách theo NHỊP — chọn "tách sau nhịp k" (keepBeatCount), không tách theo shot.';
/** E1-P2 (R8): event v1 tách theo nhịp ⇒ từ chối. CHỮ Y HỆT BE. */
export const SPLIT_BEAT_REJECT_V1 = 'Event dạng cũ (v1) tách theo SHOT (keepShotCount), không tách theo nhịp.';

export interface EventRef { sceneIdx: number; eventIdx: number }
export type BeatSeqItem =
  | { kind: 'beat'; beat: FilmActionBeat }
  | { kind: 'line'; line: FilmDialogueLine; unanchored?: boolean };
type ScenesLite = { events: { beatMeta?: FilmEventBeatMeta | null }[] }[];

export const isBeatMetaV2 = (bm: unknown): boolean =>
  !!bm && typeof bm === 'object' && !Array.isArray(bm) && (bm as { schemaVersion?: unknown }).schemaVersion === 2;

/**
 * Chuỗi hiển thị: với n = 0..N, các câu `afterActionBeatOrder === n` (giữ `orderIndex`) rồi tới nhịp n+1.
 * Câu chưa neo (null) hoặc neo ngoài 0..N dồn CUỐI với cờ `unanchored` — UI gắn nhãn "chưa neo nhịp".
 */
export const buildBeatSequence = (beats: FilmActionBeat[] | null | undefined, lines: FilmDialogueLine[] | null | undefined): BeatSeqItem[] => {
  const bs = [...(beats || [])].sort((a, b) => a.order - b.order);
  const ls = [...(lines || [])].sort((a, b) => a.orderIndex - b.orderIndex);
  const n = bs.length;
  const anchorOf = (l: FilmDialogueLine) => {
    const a = l.afterActionBeatOrder;
    return typeof a === 'number' && Number.isInteger(a) && a >= 0 && a <= n ? a : null;
  };
  const out: BeatSeqItem[] = [];
  for (let k = 0; k <= n; k++) {
    ls.filter((l) => anchorOf(l) === k).forEach((line) => out.push({ kind: 'line', line }));
    if (k < n) out.push({ kind: 'beat', beat: bs[k] });
  }
  ls.filter((l) => anchorOf(l) === null).forEach((line) => out.push({ kind: 'line', line, unanchored: true }));
  return out;
};

/** Hook = event CUỐI (theo thứ tự hiển thị) CÓ dòng nguồn — event rỗng chèn tay ở đuôi không thay hook (Q10). */
export const finalNarrativeEventRef = (scenes: ScenesLite): EventRef | null => {
  for (let si = scenes.length - 1; si >= 0; si--) {
    const evs = scenes[si].events || [];
    for (let ei = evs.length - 1; ei >= 0; ei--) {
      if ((evs[ei].beatMeta?.sourceLines || []).length) return { sceneIdx: si, eventIdx: ei };
    }
  }
  return null;
};

const WEIGHT_RANK: Record<string, number> = { TRANSITION: 0, NORMAL: 1, PEAK: 2 };
const weightOf = (w: unknown): string => {
  const u = String(w || '').toUpperCase();
  return u in WEIGHT_RANK ? u : 'NORMAL';
};
const sortedUnique = (xs: number[]) => Array.from(new Set(xs.filter((n) => Number.isInteger(n)))).sort((a, b) => a - b);

/** Gộp A+B: nhịp = A ++ B (đánh lại order), dòng nguồn hợp, weight mức cao hơn, field khác của A; anchorShift = số nhịp A. */
export const mergeBeatMetaV2 = (a: FilmEventBeatMeta, b: FilmEventBeatMeta): { beatMeta: FilmEventBeatMeta; anchorShift: number } => {
  const aBeats = a?.actionBeats || [];
  const bBeats = b?.actionBeats || [];
  const wa = weightOf(a?.dramaticWeight);
  const wb = weightOf(b?.dramaticWeight);
  return {
    beatMeta: {
      ...a,
      schemaVersion: 2,
      actionBeats: [...aBeats, ...bBeats].map((x, i) => ({ ...x, order: i + 1 })),
      sourceLines: sortedUnique([...(a?.sourceLines || []), ...(b?.sourceLines || [])]),
      dramaticWeight: WEIGHT_RANK[wa] >= WEIGHT_RANK[wb] ? wa : wb,
    },
    anchorShift: aBeats.length,
  };
};

export const shiftAnchor = (anchor: number | null | undefined, shift: number): number | null =>
  anchor === null || anchor === undefined ? null : anchor + shift;

/** v1+v1 ⇒ cho (như cũ, không xét hook) · v1+v2 ⇒ từ chối · v2+v2 ⇒ từ chối khi A hoặc B là hook. */
export const canMergeV2 = (scenes: ScenesLite, a: EventRef, b: EventRef): { ok: boolean; reason?: string } => {
  const ea = scenes[a.sceneIdx]?.events?.[a.eventIdx];
  const eb = scenes[b.sceneIdx]?.events?.[b.eventIdx];
  if (!ea || !eb) return { ok: false, reason: 'Không tìm thấy event để gộp.' };
  const av2 = isBeatMetaV2(ea.beatMeta);
  const bv2 = isBeatMetaV2(eb.beatMeta);
  if (!av2 && !bv2) return { ok: true };
  if (av2 !== bv2) return { ok: false, reason: MERGE_REJECT_MIXED };
  const hook = finalNarrativeEventRef(scenes);
  const isHook = (r: EventRef) => !!hook && hook.sceneIdx === r.sceneIdx && hook.eventIdx === r.eventIdx;
  if (isHook(a) || isHook(b)) return { ok: false, reason: MERGE_REJECT_HOOK };
  return { ok: true };
};

/** Event v2 RỖNG cho chèn tay sau event v2 (khớp BE `emptyBeatMetaV2`). */
export const emptyBeatMetaV2 = (): FilmEventBeatMeta => ({
  initiator: '', actionVerbs: {}, tactic: '', domesticEvent: '', emotionalEvent: '',
  schemaVersion: 2, actionBeats: [], sourceLines: [],
});

// ══════════ 260923 E1-P2 — bản FE của ranh giới tách + chia event theo nhịp (CÙNG luật, cùng bộ ca test với BE) ══════════
const minOf = (xs: number[]) => xs.reduce((a, b) => (b < a ? b : a), Infinity);
const maxOf = (xs: number[]) => xs.reduce((a, b) => (b > a ? b : a), -Infinity);

/**
 * Ranh giới tách hợp lệ (R5): cắt SAU nhịp k ⇔ 1 ≤ k ≤ n−1, mọi nhịp có ≥1 dòng, mọi dòng là số nguyên thuộc event, và
 * `max(dòng nhịp 1..k) < min(dòng nhịp k+1..n)`. Một nhịp khai dòng ngoài event ⇒ không có k nào.
 */
export const validSplitPoints = (beats: FilmActionBeat[] | null | undefined, eventSourceLines: number[] | null | undefined): number[] => {
  const bs = beats || [];
  if (bs.length < 2) return [];
  const inEvent = new Set(eventSourceLines || []);
  for (const b of bs) {
    const ls = b?.sourceLines || [];
    if (!ls.length || ls.some((n) => !Number.isInteger(n) || !inEvent.has(n))) return [];
  }
  const sufMin: number[] = new Array(bs.length + 1).fill(Infinity);
  for (let i = bs.length - 1; i >= 0; i--) sufMin[i] = Math.min(sufMin[i + 1], minOf(bs[i].sourceLines));
  const out: number[] = [];
  let preMax = -Infinity;
  for (let k = 1; k < bs.length; k++) {
    preMax = Math.max(preMax, maxOf(bs[k - 1].sourceLines));
    if (preMax < sufMin[k]) out.push(k);
  }
  return out;
};

/** `action` của một nửa = nối chữ các nhịp (R7). */
export const actionOfBeats = (beats: FilmActionBeat[]): string =>
  (beats || []).map((b) => String(b?.text || '').trim()).filter(Boolean).join(' ');

/**
 * Chia event khi tách sau nhịp k (R7): `T = min(dòng nhịp k+1)`; nhịp 1..k + dòng nguồn `< T` ⇒ nửa trước; còn lại ⇒ nửa
 * sau (nhịp đánh lại từ 1). Thoại: neo ≤k và NULL ở lại; neo >k sang nửa sau, neo −k; `orderIndex` đánh lại. k sai ⇒ throw.
 */
export const planSplitByBeat = <L extends { orderIndex: number; afterActionBeatOrder?: number | null }>(
  beats: FilmActionBeat[], eventSourceLines: number[], lines: L[], keepBeatCount: number,
): { first: { beats: FilmActionBeat[]; sourceLines: number[]; lines: L[] }; second: { beats: FilmActionBeat[]; sourceLines: number[]; lines: L[] }; threshold: number } => {
  const ks = validSplitPoints(beats, eventSourceLines);
  if (!Number.isInteger(keepBeatCount) || !ks.includes(keepBeatCount)) {
    throw new Error(`Không tách được sau nhịp ${keepBeatCount} — ranh giới hợp lệ: ${ks.length ? ks.join(', ') : 'không có'}`);
  }
  const k = keepBeatCount;
  const threshold = minOf(beats[k].sourceLines);
  const renum = (bs: FilmActionBeat[]) => bs.map((b, i) => ({ ...b, order: i + 1 }));
  const sorted = [...(lines || [])].sort((a, b) => a.orderIndex - b.orderIndex);
  const stays = (l: L) => l.afterActionBeatOrder === null || l.afterActionBeatOrder === undefined || l.afterActionBeatOrder <= k;
  const src = Array.from(new Set((eventSourceLines || []).filter((n) => Number.isInteger(n)))).sort((a, b) => a - b);
  return {
    first: { beats: renum(beats.slice(0, k)), sourceLines: src.filter((n) => n < threshold), lines: sorted.filter(stays).map((l, i) => ({ ...l, orderIndex: i + 1 })) },
    second: {
      beats: renum(beats.slice(k)),
      sourceLines: src.filter((n) => n >= threshold),
      lines: sorted.filter((l) => !stays(l)).map((l, i) => ({ ...l, orderIndex: i + 1, afterActionBeatOrder: (l.afterActionBeatOrder as number) - k })),
    },
    threshold,
  };
};

// ══════════ 260923 E1-P3 — bản FE của "event có cần shot không" (CÙNG luật, cùng bộ ca test với BE `filmBeatPacking`) ══════════
type ShotNeedLine = { dialogueMode?: string | null; textEn?: string | null };
const SHOT_MODES = new Set(['ON_CAMERA', 'OFF_CAMERA', 'NARRATOR']);

/** Số câu CẦN một shot: có chữ và mode ∈ {ON_CAMERA, OFF_CAMERA, NARRATOR} (≡ mode ≠ NONE; tính cả V.O.). */
export const linesNeedingShot = (lines: ShotNeedLine[] | null | undefined): number =>
  (lines || []).filter((l) => !!String(l?.textEn ?? '').trim() && SHOT_MODES.has(String(l?.dialogueMode))).length;

/** Event có cần shot không: v1 LUÔN cần; v2 ⇔ có nhịp hoặc có câu cần shot (⇔ ngân sách shot BE > 0). */
export const needsShots = (ev: { beatMeta?: unknown; dialogueLines?: ShotNeedLine[] | null }): boolean => {
  if (!isBeatMetaV2(ev.beatMeta)) return true;
  return ((ev.beatMeta as FilmEventBeatMeta).actionBeats || []).length > 0 || linesNeedingShot(ev.dialogueLines) > 0;
};

/**
 * Chữ phụ của nút "Add shot" (S8, B4). Event v2: ngân sách MỀM do BE tính (`beatTiming.shotBudget`) — cảnh báo khi đã
 * có ≥ ngân sách + 2 shot, KHÔNG khuyên tách (tách = xoá shot hai nửa); event nháp chưa có `beatTiming` ⇒ không chữ tới
 * khi Save (P7). Event v1: giữ nguyên chữ cũ.
 */
export const addShotHint = (ev: { beatMeta?: unknown; shots?: unknown[] | null; beatTiming?: { shotBudget?: number } | null }): string => {
  const n = (ev.shots || []).length;
  if (isBeatMetaV2(ev.beatMeta)) {
    const b = ev.beatTiming?.shotBudget;
    return typeof b === 'number' && n >= b + 2 ? `(⚠️ vượt ngân sách mềm (~${b} shot))` : '';
  }
  return n >= 3 ? '(⚠️ already 3 — over the one-generation budget, SPLIT the event)' : '';
};
